/**
 * The application frame, following the brand guide's page anatomy: a Djupet header bar with the
 * 2px Droppen accent seam, an Ångan-tinted work area, and white cards inside it.
 */
import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";

import logoUrl from "../../../Visualguideline/assets/logo/NordiskaBrand_logo_primary_white.svg";
import { useAuth } from "../auth";
import { Kbd } from "./primitives";

export type NavigationItem = {
  to: string;
  label: string;
  /** Shown while the area is still unbuilt, so the navigation tells the truth. */
  pending?: string;
  /** Filtered out of navigation, palette and routes for non-admins. */
  adminOnly?: boolean;
};

export function AppShell({
  navigation,
  onOpenPalette,
  children,
}: {
  navigation: NavigationItem[];
  onOpenPalette: () => void;
  children: ReactNode;
}) {
  const { auth, signOut } = useAuth();
  const { pathname } = useLocation();

  // Right surface for the right work (WP0.13, adjusted after the owner click-through):
  // reading surfaces keep the comfortable centered 900px measure, but writing and
  // administration surfaces use the full window, left-aligned — the page tree then sits
  // right next to the main navigation and the editor takes everything that remains.
  // The project room is /projekt/:id; the project list keeps the narrow measure.
  const wide =
    pathname.startsWith("/sidor") ||
    pathname.startsWith("/admin") ||
    /^\/projekt\/.+/.test(pathname);

  return (
    <div className="grid h-full grid-rows-[auto_1fr]">
      <a className="nb-skip-link" href="#huvudinnehall">
        Hoppa till innehållet
      </a>

      <header className="nb-chrome flex items-center gap-s6 px-s6 py-s4">
        {/* Clear space around the logo equals the symbol height, per the brand guide. */}
        <img src={logoUrl} alt="Nordiska Brand" className="h-s7 w-auto" />
        <span className="text-title font-bold">Noteshelf</span>

        <button
          type="button"
          onClick={onOpenPalette}
          className="ml-auto flex items-center gap-s4 rounded-control border border-line-strong px-s5 py-s2 text-body text-on-deep opacity-80 transition-[opacity] duration-control ease-nb hover:opacity-100"
        >
          <span>Sök eller gå till …</span>
          <Kbd>Ctrl + K</Kbd>
        </button>

        {auth.status === "signed_in" && (
          <div className="flex items-center gap-s4">
            <span className="hidden text-body text-on-deep opacity-80 md:inline">
              {auth.user.display_name}
            </span>
            <button
              type="button"
              onClick={() => void signOut()}
              className="rounded-control border border-line-strong px-s4 py-s2 text-small text-on-deep opacity-80 transition-[opacity] duration-control ease-nb hover:opacity-100"
            >
              Logga ut
            </button>
          </div>
        )}
      </header>

      <div className="grid min-h-0 grid-cols-1 md:grid-cols-[220px_1fr]">
        {/* The page tree replaces this list in WP0.4; mobile navigation arrives with the PWA. */}
        <nav
          aria-label="Huvudnavigering"
          className="hidden border-r border-line bg-surface-0 p-s5 md:block"
        >
          <ul className="grid gap-s2">
            {navigation.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `flex items-center justify-between gap-s4 rounded-control px-s4 py-s3 text-body transition-[background] duration-control ease-nb ${
                      isActive
                        ? "bg-surface-1 font-medium text-ink shadow-card"
                        : "text-muted hover:bg-surface-1"
                    }`
                  }
                >
                  <span>{item.label}</span>
                  {item.pending && (
                    <span className="nb-num text-overline uppercase text-muted">{item.pending}</span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <main id="huvudinnehall" className="min-w-0 overflow-y-auto p-s6">
          <div className={`grid gap-s6 ${wide ? "w-full" : "mx-auto max-w-[900px]"}`}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
